import { useEffect, useRef, useState } from 'react'

import { useSession } from 'next-auth/react'
import Image from 'next/image'

import flatten from 'lodash/flatten'
import useSWR from 'swr'

import { getCustomerFavorites } from '@lib/operations/customer-favorites'
import { getProductVariantInfo } from '@lib/operations/product'

import Spinner from '@components/Spinner'
import NoProductImage from '@components/products/NoProductImage'

import PastBoxesProductCard from './PastBoxesProductCard'

// TODO: real types
type PastBoxReviewSliderProps = {
	box: any[]
	mostRecentBoxView?: boolean
}
export default function PastBoxReviewSlider({
	box,
	mostRecentBoxView = false
}: PastBoxReviewSliderProps) {
	const [products, setProducts] = useState([])

	const [loading, setLoading] = useState(true)
	const { data: session } = useSession()

	const { data: favorites } = useSWR<string[] | null>(
		[(session as any)?.shopifyAccessToken as string, 'favorites'],
		getCustomerFavorites,
		{ revalidateIfStale: true }
	)

	// Fetch products for this specific box
	useEffect(() => {
		setLoading(true)

		const getProducts = async (productVariantIds: string[]) => {
			const productsByVariant = await getProductVariantInfo(
				productVariantIds.filter((id) => id && !id?.includes('magento_sku_'))
			)

			// Rebuild the array
			const theBox = box.map((boxItem) => {
				const product = productsByVariant.find(
					(prd: any) =>
						prd?.id === boxItem.fullSizeVariantId ||
						// accounting for fullSizeVariantId being null
						prd?.id === boxItem.variantId
				)
				return { ...boxItem, productDetails: product } || boxItem
			}).filter((boxItem) => boxItem.productDetails?.planDetails === null);

			setProducts(theBox as any)
		}

		const productVariantIds = flatten(
			box?.map(({ fullSizeVariantId, variantId }) => {
				// accounting for fullSizeVariantId being null
				const variants = [variantId]
				if (fullSizeVariantId) variants.push(fullSizeVariantId)
				return variants
			})
		)
		getProducts(productVariantIds)
		setLoading(false)

		// Set loading back to true on close
		return () => setLoading(true)
	}, [])

	// Active product image
	const [activeItemIndex, setActiveItemIndex] = useState(0)

	// Scroll container ref
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	// Set active item back to first on unmount
	useEffect(() => () => setActiveItemIndex(0), [setActiveItemIndex])

	const handleScrollIntoView = (index: number) => {
		setActiveItemIndex(index)

		scrollContainerRef.current?.children?.[index]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'center'
		})
	}

	if (loading) return <Spinner />

	return (
		<div className="px-8 pt-6 relative">
			{mostRecentBoxView && (
				<h3 className="text-2xl text-left title flex gap-2 items-center">
					<i className="fa-thin fa-box-open-full" /> 
					<span>Your most recent box</span>
				</h3>
			)}

			{products && products.length ? (
				<>
					<ul className="flex justify-left mt-10 gap-x-3 overflow-scroll max-sm:hidden">
						{products.map((product: any, i: number) => {
							// "null" products have come through during development;
							// this is trying to account for that and not show it
							if (product?.imageUrl && product?.productDetails)
								return (
									<li key={product?.variantId}>
										<button
											type="button"
											className={`flex items-center justify-center w-12 h-12 bg-white rounded-sm border transition duration-200 ease-in-out ${
												activeItemIndex === i ? 'border-bbx-spark' : 'border-transparent'
											}`}
											onClick={() => handleScrollIntoView(i)}
										>
											{product.imageUrl && (
												<Image
													src={product.imageUrl}
													alt={product.title}
													width={64}
													height={64}
													style={{ objectFit: 'contain' }}
												/>
											)}
										</button>
									</li>
								)
							else if (product.imageUrl && !product.productDetails)
								return (
									<li key={product?.variantId}>
										{product.imageUrl && (
											<div className="flex items-center justify-center w-12 h-12 transition duration-200 ease-in-out bg-white border rounded-sm opacity-30">
												<Image
													src={product.imageUrl}
													alt={product.title}
													width={64}
													height={64}
													className="object-contain"
												/>
											</div>
										)}
									</li>
								)

							return (
								// eslint-disable-next-line react/no-array-index-key
								<div key={i} className="w-[44px] h-[44px] opacity-50">
									<NoProductImage classNames="bg-white" />
								</div>
							)
						})}
					</ul>

					{/* Slider */}
					<div className="flex justify-center pb-2 -mx-8 after:absolute after:inset-0 after:bg-gradient-to-l after:from-white after:to-transparent after:pointer-events-none after:w-[80px] after:right-0 after:left-auto max-sm:after:w-0">
						<div
							className="flex pb-6 mt-6 overflow-x-auto lg:pb-14 gap-x-4 lg:gap-x-8 snap-x snap-mandatory scroll-px-4 lg:scroll-px-8"
							ref={scrollContainerRef}
						>
							{products.map((product: any, i: number, arr: any[]) => {
								const { productDetails, imageUrl, fullSizeVariantId } = product || {}

								if (product)
									return (
										<PastBoxesProductCard
											product={{
												valid: productDetails !== undefined,
												imageUrl,
												fullSizeVariantId,
												...productDetails,
												...(box?.[i]?.productVariantReasons
													? { recommendationTags: box[i].productVariantReasons }
													: [])
											}}
											className={`${i === 0 ? 'ml-8' : ''} ${i === arr.length - 1 ? 'mr-8' : ''}`}
											index={i}
											intersectionRoot={scrollContainerRef}
											activeItemIndex={activeItemIndex}
											setActiveItemIndex={setActiveItemIndex}
											key={productDetails?.id || product?.variantId}
											isFavorite={favorites ? favorites?.includes(product?.productId) : undefined}
										/>
									)

								return null
							})}
						</div>
					</div>
				</>
			) : (
				// Empty state
				<div className="flex flex-col items-center justify-center gap-6 px-4 py-32 text-center">
					<i className="text-5xl text-gray-400 fa-thin fa-store-slash" />
					<span className="text-sm text-gray-400 font-medium max-w-[90px]">
						No products in this box
					</span>
				</div>
			)}
		</div>
	)
}
